/* eslint-disable @typescript-eslint/no-explicit-any */
// src/service/securityService.ts
import { api } from "../utils/api";

export interface SecurityLog {
  id: string;
  action: string;
  severity: string;
  timestamp: string;
  ipAddress?: string;
  userAgent?: string;
  details?: any;
}

export interface UserSecurityData {
  lastLogin: string | null;
  passwordChangedAt: string | null;
  failedLoginAttempts: number;
  isLocked: boolean;
  lockUntil: string | null;
  twoFactorEnabled: boolean;
  activeSessions: number;
  securityLogs: SecurityLog[];
  emailVerified: boolean;
  accountCreated: string;
  securityAlertsEnabled?: boolean;
  loginAlertsEnabled?: boolean;
}

export interface ActiveSession {
  id: string;
  userAgent: string;
  ipAddress: string;
  createdAt: string;
  expiresAt: string;
  isCurrent: boolean;
  deviceType: string;
  lastActive?: string;
}
export interface TwoFactorSetup {
  manualEntryCode: string;
  secret: string;
  qrCode: string;
  backupCodes: string[];
}

export interface SecuritySummary {
  period: string;
  totalEvents: number;
  highRiskEvents: number;
  loginActivity: number;
  passwordChanges: number;
  sessionsRevoked: number;
  suspiciousActivities: number;
  riskLevel: string;
  lastUpdated: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface SecuritySettings {
  securityAlertsEnabled: boolean;
  loginAlertsEnabled: boolean;
  sessionTimeout: number;
  maxLoginAttempts: number;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class SecurityService {
  async getUserSecurityData(): Promise<UserSecurityData> {
    try {
      const response = await api.get<any>("/security/overview");

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(response.error || "Failed to fetch security data");
        }
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error fetching security data:", error);
      throw new Error(
        "Failed to fetch security data. Please check if security routes are configured.",
      );
    }
  }

  async getActiveSessions(): Promise<ActiveSession[]> {
    try {
      const response = await api.get<any>("/security/sessions");

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(response.error || "Failed to fetch active sessions");
        }
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error fetching active sessions:", error);
      throw error;
    }
  }

  async getSecuritySummary(): Promise<SecuritySummary> {
    try {
      const response = await api.get<any>("/security/summary");

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(response.error || "Failed to fetch security summary");
        }
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error fetching security summary:", error);
      throw error;
    }
  }

  async getSecurityLogs(
    page: number = 1,
    limit: number = 50,
  ): Promise<{
    logs: SecurityLog[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    try {
      const response = await api.get<any>(
        `/security/logs?page=${page}&limit=${limit}`,
      );

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(response.error || "Failed to fetch security logs");
        }
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error fetching security logs:", error);
      throw error;
    }
  }

  async revokeSession(sessionId: string): Promise<{ message: string }> {
    try {
      const response = await api.post<any>(
        `/security/sessions/revoke/${sessionId}`,
        {},
      );

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(response.error || "Failed to revoke session");
        }
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error revoking session:", error);
      throw error;
    }
  }

  async revokeAllSessions(): Promise<{
    sessionsRevoked: any;
    message: string;
    revokedCount: number;
  }> {
    try {
      const response = await api.post<any>("/security/sessions/revoke-all", {});

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(response.error || "Failed to revoke all sessions");
        }
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error revoking all sessions:", error);
      throw error;
    }
  }

  async changePassword(
    passwordData: ChangePasswordData,
  ): Promise<{ message: string }> {
    try {
      const response = await api.post<any>(
        "/security/change-password",
        passwordData,
      );

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(response.error || "Failed to change password");
        }
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error changing password:", error);
      throw error;
    }
  }

  async updateSecuritySettings(
    settings: Partial<SecuritySettings>,
  ): Promise<SecuritySettings> {
    try {
      const response = await api.put<any>("/security/settings", settings);

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(
            response.error || "Failed to update security settings",
          );
        }
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error updating security settings:", error);
      throw error;
    }
  }

  // Two-factor authentication methods (if you decide to integrate them)
  async enableTwoFactor(): Promise<{
    manualEntryCode: string;
    secret: string;
    qrCode: string;
    backupCodes: string[];
  }> {
    try {
      const response = await api.post<any>("/two-factor/enable", {});

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(
            response.error || "Failed to enable two-factor authentication",
          );
        }
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error enabling two-factor authentication:", error);
      throw error;
    }
  }

  async verifyTwoFactor(
    token: string,
  ): Promise<{ message: string; backupCodes: string[] }> {
    try {
      const response = await api.post<any>("/two-factor/verify", { token });
      console.log("2FA Verification Response:", response);

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(
            response.error || "Failed to verify two-factor authentication",
          );
        }
        return response.data;
      } else {
        // Handle case where response is the direct data
        if (response.error) {
          throw new Error(response.error);
        }
        return response;
      }
    } catch (error) {
      console.error("Error verifying two-factor authentication:", error);

      // Type guard to check if error is an instance of Error
      if (error instanceof Error) {
        // Provide more specific error messages
        if (error.message?.includes("400")) {
          throw new Error(
            "Invalid verification code. Please check and try again.",
          );
        } else if (error.message?.includes("429")) {
          throw new Error(
            "Too many attempts. Please wait before trying again.",
          );
        } else if (error.message?.includes("500")) {
          throw new Error(
            "Server error during verification. Please try again.",
          );
        }
      }

      // For non-Error objects or unknown error types
      throw new Error(
        "An unexpected error occurred during two-factor verification.",
      );
    }
  }

  async disableTwoFactor(
    token: string,
    password: string,
  ): Promise<{ message: string }> {
    try {
      const response = await api.post<any>("/two-factor/disable", {
        token,
        password,
      });

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(
            response.error || "Failed to disable two-factor authentication",
          );
        }
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error disabling two-factor authentication:", error);
      throw error;
    }
  }

  async getTwoFactorStatus(): Promise<{
    enabled: boolean;
    createdAt?: string;
  }> {
    try {
      const response = await api.get<any>("/two-factor/status");

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(response.error || "Failed to get two-factor status");
        }
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error getting two-factor status:", error);
      throw error;
    }
  }

  async regenerateBackupCodes(): Promise<{ backupCodes: string[] }> {
    try {
      const response = await api.post<any>(
        "/two-factor/backup-codes/regenerate",
        {},
      );

      if (response.success !== undefined) {
        if (!response.success) {
          throw new Error(
            response.error || "Failed to regenerate backup codes",
          );
        }
        return response.data;
      } else {
        return response;
      }
    } catch (error) {
      console.error("Error regenerating backup codes:", error);
      throw error;
    }
  }
}

// Create and export a singleton instance
export const securityService = new SecurityService();
export default securityService;
